import { useState, useEffect, useCallback } from "react";
import { Loader2, Target } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { errMsg } from "@/components/webadvisor/LeadDetails";
import { CompetitorFinder } from "@/components/webadvisor/CompetitorFinder";
import { CompetitorResults } from "@/components/webadvisor/CompetitorResults";

export const CompetitorIntel = ({ auditId, categories }) => {
  const [s, setS] = useState(null);
  const load = useCallback(() => api.get(`/api/webadvisor/audit/${auditId}/competitors`).then(r => setS(r.data)).catch(() => setS({})), [auditId]);
  useEffect(() => { load(); }, [load]);
  const running = s?.status === "running";
  useEffect(() => {
    if (!running) return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [running, load]);
  useEffect(() => { if (s?.status === "failed" && s.error) toast.error(s.error); }, [s?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const scan = async (list) => {
    try { await api.post(`/api/webadvisor/audit/${auditId}/competitors`, { competitors: list.map(c => ({ url: c.url, name: c.name || "", rating: c.rating ?? null, reviews: c.reviews ?? null, address: c.address || "" })) }); setS(x => ({ ...x, status: "running" })); }
    catch (e) { toast.error(errMsg(e, "Competitor scan failed")); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6 shadow-sm space-y-5" data-testid="competitor-section">
      <div>
        <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2"><Target className="w-5 h-5 text-purple-600" />Competitors — what they're doing</h2>
        <p className="text-sm text-slate-500">Find nearby competitors, pick the ones to compare, and we'll scan their sites, spot the tools they use, and tell you how to beat them. Results appear in the audit PDF, the customer portal and the AI growth plan.</p>
      </div>
      {!s ? <Loader2 className="w-5 h-5 animate-spin text-slate-400" /> : (
        <>
          <CompetitorFinder auditId={auditId} googleEnabled={s.google_enabled} busy={running} onScan={scan} />
          {running && <p className="text-sm text-purple-700 flex items-center gap-2" data-testid="competitor-running"><Loader2 className="w-4 h-4 animate-spin" />Scanning competitors and writing the analysis — about a minute…</p>}
          {s.comparison && <CompetitorResults data={s.comparison} categories={categories} />}
        </>
      )}
    </div>
  );
};
